<script setup>
import { computed, watch } from 'vue'
import { SpanInput } from '@addictivegroup/addictive-datetime'
import { toLocalInput, fromLocalInput } from '@/lib/format.js'
import { useSession } from '@/stores/session.js'

/**
 * A meeting's start and end, held together (addictive-datetime): the end moves with the start,
 * its length a field of its own. Instants in the person's zone; a whole-day meeting runs from
 * midnight to the midnight after its last day, as calendars (Microsoft 365) keep it.
 */
const props = defineProps({
  start: { type: String, default: null },
  end: { type: String, default: null },
  allDay: Boolean,
  disabled: Boolean,
  invalid: Boolean,
})
const emit = defineEmits(['update:start', 'update:end'])

const addDays = (date, n) => {
  const d = new Date(`${date}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}
const startLocal = computed(() => toLocalInput(props.start) || null)
const endLocal = computed(() => toLocalInput(props.end) || null)
const startValue = computed(() => (props.allDay ? startLocal.value?.slice(0, 10) ?? null : startLocal.value))
// The last day of a whole-day meeting: the day before the midnight it ends at.
const endValue = computed(() => {
  if (!props.allDay) return endLocal.value
  if (!endLocal.value) return null
  const [date, time] = endLocal.value.split('T')
  return time === '00:00' && startValue.value && date > startValue.value ? addDays(date, -1) : date
})

const instant = (value, end = false, allDay = props.allDay) => {
  if (!value) return null
  if (!allDay) return fromLocalInput(value)
  return fromLocalInput(`${end ? addDays(value, 1) : value}T00:00`)
}

// "All day" ticked: the days it covers, from midnight; unticked: its first day from the start of
// work, for an hour.
const session = useSession()
watch(() => props.allDay, (allDay) => {
  if (!startLocal.value) return
  const first = startLocal.value.slice(0, 10)
  if (allDay) {
    const last = endLocal.value ? endLocal.value.slice(0, 10) : first
    emit('update:start', instant(first, false, true))
    emit('update:end', instant(last < first ? first : last, true, true))
    return
  }
  const start = `${first}T${session.me?.settings?.work_start || '08:00'}`
  const [h, m] = start.slice(11).split(':').map(Number)
  emit('update:start', fromLocalInput(start))
  emit('update:end', fromLocalInput(`${first}T${String(Math.min(23, h + 1)).padStart(2, '0')}:${String(m).padStart(2, '0')}`))
})
</script>

<template>
  <SpanInput
    :start="startValue"
    :end="endValue"
    :all-day="allDay"
    :disabled="disabled"
    :invalid="invalid"
    data-testid="activity-span"
    @update:start="(v) => emit('update:start', instant(v))"
    @update:end="(v) => emit('update:end', instant(v, true))"
  />
</template>
